/*
 * File: CustomerIdFilter.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 21 Dec 2021 2:24:52 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Space, Spin } from 'antd';
import { FancyInput } from 'components/atoms/FancyInput';
import { useFormikContext } from 'formik';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { ChangeEvent, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomerData, setFirstChunkNumber, setSecondChunkNumber } from 'redux/app/app-slice';
import { ReduxState } from 'redux/rootReducer';
import { setFilterCustomerId } from 'redux/salesOrder/customerSelectionFiltersSlice';
import { useDebouncedCallback } from 'use-debounce';

export const CustomerIdFilter = (): JSX.Element => {
  const {
    values: { customerId: customerAccount }
  } = useFormikContext<SalesOrderPayloadModel>();

  const isDisabled = Boolean(customerAccount);

  const dispatch = useDispatch();

  const { cachedCustomerList, isLazyFetchLoading } = useSelector((state: ReduxState) => state.cachedCustomerList);

  /* ****************** API/Hooks ****************** */

  const memoizedResponse = useMemo(() => cachedCustomerList, [cachedCustomerList]);

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  const handleInputChange = useDebouncedCallback((ev: ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.value) {
      dispatch(setCustomerData(cachedCustomerList?.slice(0, 100)));

      return;
    }

    if (memoizedResponse?.filter((customer) => customer.customerAccount.includes(ev.target.value))) {
      dispatch(setFirstChunkNumber(100));
      dispatch(setSecondChunkNumber(200));
      dispatch(setCustomerData(memoizedResponse?.filter((customer) => customer.customerAccount.toLowerCase().includes(ev.target.value.toLowerCase()))));
    }
  }, 600);

  useEffect(() => {
    return (): void => {
      dispatch(setFilterCustomerId(undefined));
    };
  }, [dispatch]);

  /* ****************** Render ****************** */
  return (
    <Space direction="vertical" size="small" style={{ marginLeft: 5, marginRight: 5 }}>
      <Spin spinning={isLazyFetchLoading}>
        <FancyInput defaultValue="" disabled={isDisabled} onChange={handleInputChange} placeholder={'Customer ID'} width={150} />
      </Spin>
    </Space>
  );
};
